import React, { useState } from 'react';
import { SmartPackageSelector } from '../subscription/SmartPackageSelector';
import PromoCodeModal from '../PromoCodeModal';

/** The tips page paywall: plans picked in one click, with the promo code banner. Moved unchanged from the old TipsPage. */
const TipsPaywall: React.FC<{ trackName: string; state?: string; track?: string; date?: string }> = ({ trackName, state, track, date }) => {
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-8">
          
          {/* Promo Code Banner */}
          <div className="mb-8">
            <button
              onClick={() => setPromoModalOpen(true)}
              className="w-full flex items-center justify-between bg-gradient-to-r from-racing-green to-emerald-600 text-white rounded-xl px-6 py-4 hover:from-emerald-600 hover:to-racing-green transition-all shadow-md group"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-semibold text-sm">Have a promo code?</p>
                  <p className="text-white/80 text-xs">Click to redeem and unlock access instantly</p>
                </div>
              </div>
              <svg className="w-5 h-5 text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Hero Section with Urgency */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 mb-4">
              🔥 Racing Today • Limited Time Access
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Missing Out on {trackName} Winners?
            </h1>
            <p className="text-xl text-gray-600 mb-6 max-w-3xl mx-auto">
              Get AI-powered racing analysis and data-driven insights for {trackName} to help inform your betting strategy.
              Access professional-grade analysis to make more informed decisions.
            </p>
            
            {/* Social Proof Bar */}
            <div className="flex items-center justify-center space-x-8 text-sm text-gray-500 mb-8">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span>AI Analysis</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span>Data Insights</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span>Premium Content</span>
              </div>
            </div>
          </div>

          {/* Package Selection - Moved above sales boxes */}
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 mb-12">
            <SmartPackageSelector
              context={{
                trackName: trackName,
                trackId: (state && track ? `${state}-${track}` : undefined),
                meetingDate: date ? `${date.substring(0, 4)}-${date.substring(4, 6)}-${date.substring(6, 8)}` : undefined
              }}
              title="Choose Your Winning Package"
              description={`Get AI-generated racing analysis and data-driven insights for ${trackName} to help inform your betting strategy.`}
              compact={false}
              maxPackages={5}
              onSubscribe={() => {
                // Store track information for return after payment
                if (state && track && date) {
                  try {
                    sessionStorage.setItem('payment_return_track', JSON.stringify({
                      state,
                      track,
                      date
                    }));
                  } catch (error) {
                    console.warn('Could not store track info in sessionStorage:', error);
                  }
                }
              }}
            />
          </div>

          {/* Value Proposition Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">AI-Powered Analysis</h3>
              <p className="text-gray-600 text-sm">Advanced AI algorithms analyze vast amounts of racing data, track conditions, and historical performance patterns.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Real-Time Updates</h3>
              <p className="text-gray-600 text-sm">Live track conditions, scratchings, and last-minute insights delivered instantly.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Data-Driven Insights</h3>
              <p className="text-gray-600 text-sm">Machine learning models process complex racing data to identify patterns and generate analytical insights.</p>
            </div>
          </div>

          {/* Value Proposition */}
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 mb-12">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">AI-Generated Racing Analysis</h3>
              <p className="text-gray-600 mb-6">
                Our AI system processes extensive racing data including form guides, track conditions, weather patterns, and historical performance
                to generate comprehensive analysis for each race. Get data-driven insights to inform your betting decisions.
              </p>
              <div className="flex items-center justify-center text-sm text-gray-500">
                <svg className="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span>Advanced AI data analysis and processing</span>
              </div>
            </div>
          </div>

          {/* Risk Reduction - Removed 30-day guarantee */}
          <div className="text-center mt-8">
            <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
              Cancel Anytime
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Start your subscription with complete flexibility.
            </p>
          </div>


        </div>
      </div>
      <PromoCodeModal isOpen={promoModalOpen} onClose={() => setPromoModalOpen(false)} />
    </>
  );
};

export default TipsPaywall;
